import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useTranslate } from "ra-core";
import { useEffect, useRef } from "react";

import { useIsMobile } from "@/hooks/use-mobile";
import "./onboarding.css";
import { canShowTip } from "./onboardingLogic";
import { useOnboarding } from "./OnboardingProvider";
import type { TipStep } from "./types";

// `mobileKey` : texte propre au téléphone (« Touchez » au lieu de « Cliquez »).
export type TipSpec = { target: string; key: string; mobileKey?: string };

const selector = (target: string) => `[data-tour="${target}"]`;

/** Boutons d'une étape : pas de « Précédent » sur la première (ni donc sur une bulle d'une seule étape). */
export const tipButtons = (index: number): ("next" | "previous" | "close")[] =>
  index === 0 ? ["next", "close"] : ["next", "previous", "close"];

/** Défilement de la fenêtre et du contenu principal, pour le rendre tel quel après la bulle. */
const saveScroll = () => {
  const main = document.getElementById("main-content");
  const x = window.scrollX;
  const y = window.scrollY;
  const top = main?.scrollTop ?? 0;
  const left = main?.scrollLeft ?? 0;
  return () => {
    window.scrollTo(x, y);
    if (main?.isConnected) {
      main.scrollTop = top;
      main.scrollLeft = left;
    }
  };
};

/** Étapes dont l'élément est visible ; une bulle sans élément n'est ni montrée ni notée comme vue. */
export const tipTargetsPresent = (doc: Document, specs: TipSpec[]) =>
  specs.filter(({ target }) => {
    const node = doc.querySelector<HTMLElement>(selector(target));
    return !!node && !node.hidden && node.getClientRects().length > 0;
  });

/** Bulle d'aide d'un écran, à sa première ouverture, après la séquence d'accueil ; deux étapes au plus. */
export const useScreenTip = (step: TipStep, specs: TipSpec[], ready = true) => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { seen, loaded, introOpen, markSeen } = useOnboarding();
  const show =
    ready && loaded && !introOpen && canShowTip(seen, step, isMobile);
  // Relus au moment du minuteur : un changement de compte ou un bloc chargé après coup est pris en compte.
  const latest = useRef({ specs, translate, markSeen });
  latest.current = { specs, translate, markSeen };

  useEffect(() => {
    if (!show) return;
    let tour: ReturnType<typeof driver> | undefined;
    let leaving = false;
    // Laisse l'écran se dessiner ; jamais par-dessus une autre fenêtre ni la présentation.
    const timer = window.setTimeout(() => {
      // Une autre bulle est déjà ouverte : celle-ci attend une prochaine visite, sans être notée vue.
      if (document.body.classList.contains("driver-active")) return;
      if (
        document.querySelector(
          "[role='dialog'][data-state='open'], [role='alertdialog'], .onb-root",
        )
      )
        return;
      const { specs, translate, markSeen } = latest.current;
      const present = tipTargetsPresent(document, specs).slice(0, 2);
      if (!present.length) return;
      const t = (key: string) => translate(`resources.onboarding.tips.${key}`);
      // Driver.js fait défiler jusqu'à l'élément : la page est rendue comme elle était quand la bulle se ferme.
      const restoreScroll = saveScroll();
      tour = driver({
        showProgress: present.length > 1,
        allowClose: true,
        overlayOpacity: 0.35,
        popoverClass: isMobile ? "onb-tip onb-tip-sheet" : "onb-tip",
        nextBtnText: translate("resources.onboarding.next"),
        prevBtnText: translate("resources.onboarding.previous"),
        doneBtnText: translate("resources.onboarding.tips.done"),
        progressText: translate("resources.onboarding.tips.progress"),
        onDestroyed: () => {
          markSeen(step);
          // Changement d'écran : le défilement de l'écran suivant ne se règle pas sur celui-ci.
          if (!leaving) restoreScroll();
        },
        steps: present.map(({ target, key, mobileKey }, index) => {
          const text = isMobile && mobileKey ? mobileKey : key;
          return {
            // Sur téléphone, pas de flèche vers l'élément : un panneau en bas de l'écran.
            element: isMobile ? undefined : selector(target),
            popover: {
              title: t(`${text}.title`),
              description: t(`${text}.body`),
              showButtons: tipButtons(index),
            },
          };
        }),
      });
      tour.drive();
    }, 600);
    return () => {
      window.clearTimeout(timer);
      // Changement d'écran : la bulle ne reste pas par-dessus le suivant.
      leaving = true;
      if (tour?.isActive()) tour.destroy();
    };
  }, [show, step, isMobile]);
};
